import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { tmApi, type TmTaskView, type TmAttachmentView, type TmCommentView } from "../../api/tm";
import { TmPriorityPill } from "../../components/tm/TmPriorityPill";
import { TmStatusActionMenu } from "../../components/tm/TmStatusActionMenu";
import { TmAttachmentList } from "../../components/tm/TmAttachmentList";
import { TmCommentThread } from "../../components/tm/TmCommentThread";
import { TmTagPicker } from "../../components/tm/TmTagPicker";
import { TmWatchButton } from "../../components/tm/TmWatchButton";
import { TmSubtasksList } from "../../components/tm/TmSubtasksList";
import { TmDependenciesPanel } from "../../components/tm/TmDependenciesPanel";
import { useToast } from "../../components/Toast";
import type { ApiError } from "../../api/client";
import { intlLocale } from "../../lib/format";

/** A due date is overdue if it's in the past and the task isn't finished/cancelled. */
function dueIsOverdue(dueDate: string | null, status: string): boolean {
  if (!dueDate || status === "DONE" || status === "CANCELLED") return false;
  return new Date(dueDate) < new Date(new Date().toDateString());
}

export default function TmTaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const toast = useToast();
  const [task, setTask] = useState<TmTaskView | null>(null);
  const [attachments, setAttachments] = useState<TmAttachmentView[]>([]);
  const [comments, setComments] = useState<TmCommentView[]>([]);

  const refresh = useCallback(async () => {
    if (!id) return;
    const [t1, atts, cmts] = await Promise.all([
      tmApi.get(id), tmApi.listAttachments(id), tmApi.listComments(id),
    ]);
    setTask(t1);
    setAttachments(atts);
    setComments(cmts);
  }, [id]);

  useEffect(() => { void refresh(); }, [refresh]);

  const safeDescription = useMemo(() => {
    if (!task?.description) return null;
    return DOMPurify.sanitize(task.description);
  }, [task?.description]);

  if (!task) {
    return <div style={{ padding: 24, color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>;
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Link to="/tm/tasks" style={{ color: "var(--ink-3)", fontSize: 13, textDecoration: "none" }}>
          ← {t("tm.tasks.backToList", "Back to tasks")}
        </Link>
      </div>
      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)", flex: 1 }}>{task.title}</h1>
            <TmPriorityPill priority={task.priority} />
            <TmStatusActionMenu
              current={task.status}
              onTransition={async (next, payload) => {
                try {
                  const updated = await tmApi.transition(task.id, { toStatus: next, ...payload });
                  setTask(updated);
                  toast.success(t("tm.tasks.detail.transitioned", "Status updated."));
                } catch (e) {
                  toast.error((e as ApiError).message);
                }
              }}
            />
          </div>

          {task.projectId && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 10px", background: "var(--surface-2)",
              border: "1px solid var(--border)", borderRadius: 6,
              fontSize: 12, color: "var(--ink-2)", alignSelf: "flex-start",
            }}>
              <span style={{ color: "var(--ink-4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>
                {t("tm.tasks.detail.project", "Project")}
              </span>
              <Link to={`/tm/projects/${task.projectId}`} style={{ color: "var(--brand-primary)", textDecoration: "none", fontWeight: 600 }}>
                {task.projectName}
              </Link>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <PersonPill role={t("tm.tasks.detail.assignee", "Assignee")} name={task.assigneeName} accent="var(--brand-primary)" />
            <PersonPill role={t("tm.tasks.detail.validator", "Validator")} name={task.validatorName} accent="var(--tone-violet)" />
          </div>
          {task.blockedReason && (
            <div style={{ padding: 10, background: "var(--err-bg)", borderRadius: 6, color: "var(--err)", fontSize: 13 }}>
              ⚠ {task.blockedReason}
            </div>
          )}
          {safeDescription && (
            <div className="tm-prose"
                 // DOMPurify-sanitized HTML authored by Bankom-internal users
                 dangerouslySetInnerHTML={{ __html: safeDescription }} />
          )}
          {task.parentTaskId && (
            <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
              Subtask of{" "}
              <Link to={`/tm/tasks/${task.parentTaskId}`} style={{ color: "var(--brand-primary)", textDecoration: "none" }}>
                {task.parentTaskTitle ?? "parent task"}
              </Link>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, color: "var(--ink-3)", fontSize: 13 }}>
            <div>
              <b>{t("tm.tasks.detail.due", "Due")}:</b>{" "}
              {task.dueDate ? (
                <span style={{ color: dueIsOverdue(task.dueDate, task.status) ? "var(--err)" : "var(--ink)", fontWeight: 600 }}>
                  {task.dueDate}
                  {dueIsOverdue(task.dueDate, task.status) && (
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "var(--err)", background: "var(--err-bg)", padding: "1px 7px", borderRadius: 999 }}>
                      {t("tm.tasks.detail.overdue", "Overdue")}
                    </span>
                  )}
                </span>
              ) : "—"}
            </div>
            <div><b>{t("tm.tasks.detail.created", "Created")}:</b> {formatDateTime(task.createdAt)}</div>
          </div>

          <div>
            <div style={sectionLabel}>{t("tm.tasks.detail.tags", "Tags")}</div>
            <TmTagPicker
              taskId={task.id}
              tags={task.tags}
              onChange={async () => {
                const updated = await tmApi.get(task.id);
                setTask(updated);
              }}
            />
          </div>

          <div>
            <div style={sectionLabel}>{t("tm.tasks.detail.watchers", "Watchers")}</div>
            <TmWatchButton taskId={task.id} />
          </div>

          <div>
            <div style={sectionLabel}>{t("tm.tasks.detail.subtasks", "Subtasks")}</div>
            <TmSubtasksList parentTaskId={task.id} />
          </div>

          <div>
            <div style={sectionLabel}>{t("tm.tasks.detail.dependencies", "Dependencies")}</div>
            <TmDependenciesPanel taskId={task.id} />
          </div>

          <div>
            <div style={sectionLabel}>{t("tm.tasks.detail.attachments", "Attachments")}</div>
            <TmAttachmentList taskId={task.id} attachments={attachments} onUploaded={refresh} />
          </div>

          <div>
            <div style={sectionLabel}>{t("tm.tasks.detail.comments", "Comments")}</div>
            <TmCommentThread taskId={task.id} comments={comments} onAdded={refresh} />
          </div>

        </div>
      </div>
    </div>
  );
}

function PersonPill({ role, name, accent }: { role: string; name: string | null | undefined; accent: string }) {
  const display = name ?? "—";
  const initials = (display === "—" ? "?" : display
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase());
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 10,
      padding: "8px 12px",
      background: "var(--surface-2)", border: "1px solid var(--border)",
      borderRadius: 10, minWidth: 200,
    }}>
      <span aria-hidden style={{
        width: 30, height: 30, borderRadius: "50%",
        background: `color-mix(in srgb, ${accent} 14%, var(--surface))`, color: accent,
        display: "grid", placeItems: "center",
        fontWeight: 700, fontSize: 11, flex: "none",
      }}>{initials}</span>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: 0.4 }}>
          {role}
        </span>
        <strong style={{ fontSize: 13, color: "var(--ink)" }}>{display}</strong>
      </div>
    </div>
  );
}

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--ink-3)",
  letterSpacing: ".04em",
  textTransform: "uppercase",
  marginBottom: 8,
};

/** Renders an ISO timestamp as "15 May 2026, 14:32" in the user's locale. */
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(intlLocale(), {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    hour12: false, // EU 24-hour format — no AM/PM
  });
}
